import EditIcon from '@mui/icons-material/Edit';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { apiFetch } from '../../api/client.js';

import { GurupayIndustryTypeField } from './GurupayIndustryTypeField.js';

/**
 * Correct a business client's legal name: a typo at onboarding, or a rename the
 * company registered. Admin and compliance only (the API enforces it too).
 * The approved version stays on record; the change, with its reason, goes to
 * the client's history.
 *
 * What it does not change is said on screen: the KYB profile at the identity
 * provider keeps the name it was created with, and a real rename means the
 * company has to be screened again there under its new name.
 */

interface PartnerResult { account: string; ok: boolean; detail: string }
interface Corrected { legalName: string; previous: string; partners: PartnerResult[] }
const resend = (customerId: string, reason: string): Promise<{ partners: PartnerResult[] }> =>
  apiFetch(`/admin/customers/${customerId}/legal-name/send-to-partners`, { method: 'POST', body: { reason } });
const correct = (customerId: string, legalName: string, reason: string, industry: number | null): Promise<Corrected> =>
  apiFetch(`/admin/customers/${customerId}/legal-name`, { method: 'POST',
    body: { legalName, reason, ...(industry === null ? {} : { gurupayIndustryType: industry }) } });

interface CorrectLegalNameButtonProps {
  readonly customerId: string;
  readonly current: string;
  readonly hasGuruPay?: boolean;
  /** GuruPay industry type already on the client's file; the select starts from it. */
  readonly storedIndustry?: number | null;
}

export function CorrectLegalNameButton({ customerId, current, hasGuruPay = false, storedIndustry = null }: CorrectLegalNameButtonProps): JSX.Element {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(current);
  const [reason, setReason] = useState('');
  const [industry, setIndustry] = useState<number | null>(storedIndustry);
  const [done, setDone] = useState<Corrected | null>(null);
  const save = useMutation({
    // A stored type is not sent again unless somebody changed it.
    mutationFn: () => correct(customerId, name, reason, industry === storedIndustry ? null : industry),
    onSuccess: async (result) => {
      setDone(result);
      await queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
      await queryClient.invalidateQueries({ queryKey: ['case-file', customerId] });
    },
  });
  const again = useMutation({
    mutationFn: () => resend(customerId, reason),
    onSuccess: async (result) => {
      setDone((d) => (d === null ? d : { ...d, partners: result.partners }));
      await queryClient.invalidateQueries({ queryKey: ['case-file', customerId] });
    },
  });
  const openDialog = (): void => {
    setName(current); setReason(''); setIndustry(storedIndustry); setDone(null); save.reset(); again.reset(); setOpen(true);
  };
  const unchanged = name.replace(/\s+/g, ' ').trim() === current.trim() && industry === storedIndustry;
  const industryMissing = hasGuruPay && industry === null;

  return <>
    <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={openDialog} sx={{ ml: 1, p: 0.25, minWidth: 0 }}
      aria-label="Correct the legal name">Correct</Button>
    <Dialog open={open} onClose={() => { if (!save.isPending) setOpen(false); }} fullWidth maxWidth="sm" aria-labelledby="legal-name-title">
      <DialogTitle id="legal-name-title">Correct the legal name</DialogTitle>
      <DialogContent>
        {done === null ? <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Enter the name exactly as it appears on the company register extract. The approved application keeps the name it was approved
            with; this change and its reason go to the client&apos;s history. Screens, statements and reports show the new name.
          </Typography>
          <TextField label="Legal name" value={name} onChange={(event) => setName(event.target.value)} inputProps={{ maxLength: 200 }} autoFocus fullWidth />
          <TextField label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} multiline minRows={2}
            helperText="For example: typo at onboarding, or rename registered on 1 Oct (extract on file). At least 10 characters."
            inputProps={{ maxLength: 1000 }} fullWidth />
          {hasGuruPay && <GurupayIndustryTypeField value={industry} onChange={setIndustry}
            helperText={storedIndustry === null
              ? "GuruPay needs it to update the name on the customer's vIBAN. Choose it from the company's actual activity on the KYB file: it is a classification."
              : "On the client’s file already. Change it only if the company’s activity was classified wrongly."} />}
          {save.isError && <Alert severity="error">{save.error instanceof Error ? save.error.message : 'The name could not be corrected.'}</Alert>}
        </Stack> : <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="success">Legal name corrected from “{done.previous}” to “{done.legalName}”.</Alert>
          {done.partners.map((p) => <Alert key={p.account} severity={p.ok ? 'success' : 'error'}>
            <strong>{p.account}:</strong> {p.ok ? 'GuruPay now holds the new name (it is also what payer banks verify against).' : p.detail}
          </Alert>)}
          {again.isError && <Alert severity="error">{again.error instanceof Error ? again.error.message : 'Sending to GuruPay failed.'}</Alert>}
          <Alert severity="info">
            The KYB profile at the identity provider still carries the name it was created with. If the company really changed its name,
            update it there and have the company screened again under the new name.
          </Alert>
        </Stack>}
      </DialogContent>
      <DialogActions>
        {done === null ? <>
          <Button onClick={() => setOpen(false)} disabled={save.isPending}>Cancel</Button>
          <Button variant="contained" onClick={() => save.mutate()}
            disabled={save.isPending || unchanged || industryMissing || reason.trim().length < 10 || name.trim().length < 2}>
            {save.isPending ? 'Saving…' : 'Save'}</Button>
        </> : <>
          {done.partners.some((p) => !p.ok) && <Button onClick={() => again.mutate()} disabled={again.isPending}>
            {again.isPending ? 'Sending…' : 'Send to GuruPay again'}</Button>}
          <Button variant="contained" onClick={() => setOpen(false)}>Close</Button>
        </>}
      </DialogActions>
    </Dialog>
  </>;
}
